import { useState } from "react";
import { Heart, Video } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { StayConnected } from "@/components/StayConnected";

const RECORD_URL =
  "https://memoryplace.com/viduber/mspace/assets/3e4d689c-5b29-4f44-a735-1b28b178763f";

export default function Share() {
  const [consented, setConsented] = useState(false);

  const openRecorder = () => {
    if (!consented) return;
    window.open(RECORD_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="share-page">
      <section className="mx-auto max-w-3xl px-4 pb-24 pt-16 text-center sm:px-6 lg:pt-24">
        <Reveal>
          <Eyebrow className="text-center">Your voice matters</Eyebrow>
          <h1
            className="mt-4 font-serif text-4xl font-bold leading-[1.15] tracking-tight text-ink sm:text-5xl lg:text-6xl"
            data-testid="share-headline"
          >
            Share Your <span className="italic text-crimson">Story</span>
          </h1>
          <p
            className="mt-5 font-serif text-lg italic leading-relaxed text-cocoa sm:text-xl"
            data-testid="share-subheadline"
          >
            One story can change everything. Yours might change someone's life.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <p
            className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-cocoa sm:text-lg"
            data-testid="share-body"
          >
            Every community has people who need a hand — and people with the
            heart to give one. This is where those two find each other. Share
            your 60-second story, a need, a testimony, or a moment when someone
            showed up for you. Your words matter more than you know.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <p
            className="mt-10 font-serif text-lg italic text-ink sm:text-xl"
            data-testid="share-intro-line"
          >
            Your story matters. Share it with your community.
          </p>
          <a
            href="https://memoryplace.com/viduber/mspace/memory/efeeb70b-f8db-49ae-83d8-fdcd46d338dc"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-crimson px-10 py-4 text-base font-semibold text-ivory transition-all duration-200 hover:-translate-y-0.5 hover:bg-crimson-deep"
            data-testid="share-your-story-cta"
          >
            <Heart className="h-4 w-4" fill="currentColor" />
            Share Your Story
          </a>
        </Reveal>

        <Reveal delay={0.18}>
          <div
            className="mx-auto mt-12 max-w-2xl rounded-3xl bg-crimson px-8 py-12 text-center shadow-xl"
            data-testid="share-pull-quote"
          >
            <p className="font-serif text-xl font-bold leading-relaxed text-gold-bright sm:text-2xl">
              <span className="block">You don't have to save the world.</span>
              <span className="mt-4 block">Start with one story.</span>
              <span className="block">One person.</span>
              <span className="block">One family.</span>
              <span className="block">One school.</span>
              <span className="mt-4 block">Find the heart.</span>
              <span className="block italic">We'll help you tell the story.</span>
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div
            className="mx-auto mt-12 max-w-xl rounded-3xl border border-line bg-white p-8 shadow-xl sm:p-10"
            data-testid="share-consent-card"
          >
            <label
              className="flex cursor-pointer items-start gap-3 text-left"
              data-testid="share-consent-label"
            >
              <input
                type="checkbox"
                checked={consented}
                onChange={(e) => setConsented(e.target.checked)}
                className="mt-1 h-5 w-5 shrink-0 cursor-pointer accent-[#9E1B1B]"
                data-testid="share-consent-checkbox"
              />
              <span className="text-sm leading-relaxed text-cocoa sm:text-base">
                I give MyHeartGives permission to share my video story to help
                raise support in my community.
              </span>
            </label>

            <button
              onClick={openRecorder}
              disabled={!consented}
              className={`mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-semibold transition-all duration-200 ${
                consented
                  ? "bg-crimson text-ivory hover:-translate-y-0.5 hover:bg-crimson-deep"
                  : "cursor-not-allowed bg-line text-taupe"
              }`}
              data-testid="record-my-story-button"
            >
              <Video className="h-4 w-4" />
              Record My Story
            </button>
            {!consented && (
              <p
                className="mt-3 text-xs text-taupe"
                data-testid="share-consent-hint"
              >
                Please check the box above to begin recording.
              </p>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.3}>
          <p
            className="mt-16 flex items-center justify-center gap-3 font-serif text-xl italic text-gold sm:text-2xl"
            data-testid="share-closing-line"
          >
            <Heart className="h-5 w-5" fill="currentColor" />
            Touching one Heart to Another.
            <Heart className="h-5 w-5" fill="currentColor" />
          </p>
        </Reveal>
      </section>

      {/* COMMUNITY STORIES (GotBackup opt-in) */}
      <StayConnected
        testidPrefix="stories-optin"
        campaign="Community Stories"
        eyebrow="Stories from neighbors"
        heading={
          <>
            Stay Informed on Stories in{" "}
            <span className="italic text-crimson">Your Community</span>
          </>
        }
        subheading="New stories from people near you — a need, a testimony, a moment worth remembering — delivered with care."
        buttonLabel="Share My Story"
      />

      <Marquee
        items={[
          "Listen First",
          "Give Freely",
          "Build Together",
        ]}
      />
    </div>
  );
}
